import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';

const api = axios.create({ baseURL: `${API_BASE_URL}/api/v1` });
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CACHE_TTL_MS = 60 * 1000;

const ChannelSettingsContext = createContext(null);

export const ChannelSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({ email: true, whatsapp: false });
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);

  const fetchSettings = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetchRef.current < CACHE_TTL_MS) return;
    try {
      const res = await api.get('/channel-settings');
      const map = {};
      (res.data || []).forEach((item) => { map[item.channel] = item.enabled; });
      setSettings({ email: map.email ?? true, whatsapp: map.whatsapp ?? false });
      lastFetchRef.current = Date.now();
    } catch {
      // keep existing state on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(true); }, [fetchSettings]);

  const updateChannelSetting = useCallback(async (channel, enabled) => {
    const res = await api.put(`/channel-settings/${channel}`, { enabled });
    setSettings((prev) => ({ ...prev, [channel]: res.data.enabled }));
    lastFetchRef.current = 0; // invalidate cache
    return res.data;
  }, []);

  return (
    <ChannelSettingsContext.Provider value={{
      emailEnabled: settings.email,
      whatsappEnabled: settings.whatsapp,
      loading,
      updateChannelSetting,
      refetch: () => fetchSettings(true),
    }}>
      {children}
    </ChannelSettingsContext.Provider>
  );
};

export const useChannelSettings = () => {
  const ctx = useContext(ChannelSettingsContext);
  if (!ctx) throw new Error('useChannelSettings must be used inside ChannelSettingsProvider');
  return ctx;
};
